import React, { memo, useCallback, useEffect, useRef, useState } from "react";

import { Box, Button } from "@chakra-ui/react";
import { exportPayrollsData } from "api/business-user-management";
import { IExportPayrollsPayload } from "api/business-user-management/types";
import { ReactComponent as RefreshIcon } from "assets/icons/table-refresh-icon.svg";
import { useFilterQueryParams } from "hooks/useFilterQueryParams";
import { useUpdateQueryParams } from "hooks/useUpdateQueryParams";
import { debounce } from "lodash";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import {
  capitalizeWords,
  formatDateToYYYYMMDD,
  handleDownloadBufferData,
  haveQueryParamsChanged,
} from "utils";
import {
  NO_EXPORT_DATA_FOUND,
  NO_NEW_FILTER_MESSAGE,
  SEARCH_DEBOUNCE_TIMER,
  ZERO,
} from "utils/constants";

import {
  customErrorToast,
  customInfoToast,
  customSuccessToast,
  customWarningToast,
} from "ui/custom-toast";
import { IDropDownRef } from "ui/dropdown/types";
import { Export } from "ui/export";
import { IExportDateRangeValues, TExportType } from "ui/export/types";
import { Filter } from "ui/filter";
import {
  IDateRangeValues,
  IFilterRef,
  TFilterOption,
  TFilterValue,
  TFilterValues,
} from "ui/filter/types";
import { tableHeaderCtaCss } from "ui/table/styles";
import { TableSearchInput } from "ui/table/table-search-input";

import { DEFAULT_PAYROLLS_TABLE_PAGINATION_PARAMS } from "./const";
import { IBusinessPayrollTableHeaderCTAsProps } from "./types";

const AVAILABLE_EXPORT_OPTIONS: TExportType[] = ["csv", "pdf"];
const ALL_PAYROLL_STATUS_LIST = [
  "all",
  "draft",
  "approved",
  "in-review",
  "processing",
  "rejected",
  "completed",
];
export const BusinessPayrollsTableHeaderCTAs = memo(
  (props: IBusinessPayrollTableHeaderCTAsProps) => {
    const {
      handleGetBusinessPayrollsData,
      isLoading,
      isRefreshing,
      setIsRefreshing,
    } = props;
    const { businessId } = useParams();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const updateQueryParams = useUpdateQueryParams();
    const { isFilterError, setIsFilterError, filterRetryCountParams } =
      useFilterQueryParams({
        apiFilterRoute: "/control/business/compliance/view-payroll",
      });
    const startDate = searchParams.get("startDate");
    const endDate = searchParams.get("endDate");
    const status = searchParams.get("status");
    const search = searchParams.get("search");
    const [searchQuery, setSearchQuery] = useState(search || "");
    const [filterValues, setFilterValues] = useState<TFilterValues>({
      status: status ? status.split(",") : [],
      dateRange: {
        startDate,
        endDate,
      },
    });
    const filterRef = useRef<IFilterRef | null>(null);
    const [isFiltering, setIsFiltering] = useState(false);
    const [isExporting, setIsExporting] = useState(false);

    const isFilterDisabled = !(
      (filterValues?.status as string[])?.length > ZERO ||
      (filterValues?.dateRange as IDateRangeValues)?.startDate
    );

    const handleResetFilter = () => {
      setFilterValues({
        status: [],
        dateRange: {
          startDate: null,
          endDate: null,
        },
      });
    };

    const onBusinessPayrollsSearch = useCallback(
      debounce(async (value: string) => {
        if (value.trim() === "") {
          updateQueryParams({
            ...Object.fromEntries(new URLSearchParams(location.search)),
            search: null,
            page: String(DEFAULT_PAYROLLS_TABLE_PAGINATION_PARAMS.page),
            limit: String(DEFAULT_PAYROLLS_TABLE_PAGINATION_PARAMS.limit),
          });
          return;
        }

        updateQueryParams({
          ...Object.fromEntries(new URLSearchParams(location.search)),
          search: value,
          page: String(DEFAULT_PAYROLLS_TABLE_PAGINATION_PARAMS.page),
          limit: String(DEFAULT_PAYROLLS_TABLE_PAGINATION_PARAMS.limit),
        });
      }, SEARCH_DEBOUNCE_TIMER),
      [location]
    );

    const handleSearchQueryChange = (
      evt: React.ChangeEvent<HTMLInputElement>
    ) => {
      const { value } = evt.target;
      setSearchQuery(value);
      onBusinessPayrollsSearch(value);
    };

    const handleRefreshTable = async () => {
      setIsRefreshing(true);
      if (!businessId) {
        console.error("No business id");
        return;
      }
      if (
        !haveQueryParamsChanged({
          currentSearch: location.search,
          newParams: {},
        })
      ) {
        await handleGetBusinessPayrollsData(
          {
            businessId,
            ...DEFAULT_PAYROLLS_TABLE_PAGINATION_PARAMS,
          },
          { isRefetching: true }
        );
        setIsRefreshing(false);
      } else {
        setSearchQuery("");
        updateQueryParams({});
      }
    };

    const handleFilterValueChange = (key: string, value: TFilterValue) => {
      let newValue = value;
      if (key === "status") {
        const transactionStatus = value as string[];
        const currentTransactionStatus = filterValues?.status as string[];
        if (
          transactionStatus.includes("all") &&
          currentTransactionStatus.length < ALL_PAYROLL_STATUS_LIST.length
        ) {
          newValue = ALL_PAYROLL_STATUS_LIST;
        } else {
          newValue = transactionStatus.filter(
            (trxStatus) => trxStatus !== "all"
          );
        }
      }
      setFilterValues((prev) => ({
        ...prev,
        [key]: newValue,
      }));
    };

    const handleApplyFilter = async () => {
      const newQueryParams: Record<string, string | null> = {};
      const startDate = (filterValues.dateRange as IDateRangeValues).startDate;
      const endDate = (filterValues.dateRange as IDateRangeValues).endDate;
      const status = filterValues.status;
      if (startDate) {
        newQueryParams["startDate"] = startDate;
      } else {
        newQueryParams["startDate"] = null;
      }

      if (endDate) {
        newQueryParams["endDate"] = endDate;
      } else {
        newQueryParams["endDate"] = null;
      }

      if (Array.isArray(status) && status.length > ZERO) {
        newQueryParams["status"] = status.join(",");
      } else {
        newQueryParams["status"] = null;
      }
      newQueryParams["page"] =
        DEFAULT_PAYROLLS_TABLE_PAGINATION_PARAMS.page.toString();
      newQueryParams["limit"] =
        DEFAULT_PAYROLLS_TABLE_PAGINATION_PARAMS.limit.toString();

      setIsFilterError(false);
      updateQueryParams({
        ...newQueryParams,
        ...filterRetryCountParams,
      });
      if (
        haveQueryParamsChanged({
          currentSearch: location.search,
          newParams: newQueryParams,
        }) ||
        isFilterError
      ) {
        setIsFiltering(true);
      } else {
        customWarningToast({
          message: NO_NEW_FILTER_MESSAGE,
        });
      }
    };

    const dropDownRef = useRef<IDropDownRef | null>(null);
    const [dateRange, setDateRange] = useState<IExportDateRangeValues>({
      startDate: null,
      endDate: null,
    });
    const [exportType, setExportType] = useState<TExportType | "">("");

    const handleExportTypeChange = (
      evt: React.ChangeEvent<HTMLInputElement>
    ) => {
      const { value } = evt.target;
      setExportType(value as TExportType);
    };

    const handleDateRangeChange = (name: string, value: Date | null) => {
      if (!value) return;
      setDateRange({
        ...dateRange,
        [name]: value,
      });
    };

    const handleResetExport = () => {
      setDateRange({
        startDate: null,
        endDate: null,
      });
      setExportType("");
    };

    const handleExport = async () => {
      const { startDate, endDate } = dateRange;
      if (!startDate || !endDate || !businessId || !exportType) return;
      const formattedStartDate = formatDateToYYYYMMDD(startDate);
      const formattedEndDate = formatDateToYYYYMMDD(endDate);
      setIsExporting(true);
      const exportPayload: IExportPayrollsPayload = {
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        format: exportType,
        businessId,
      };
      try {
        const { data, error, message } =
          await exportPayrollsData(exportPayload);
        if (error) {
          customErrorToast({ message });
        } else if (data?.data) {
          const exportData = data?.data;
          if (exportData.length > ZERO) {
            handleDownloadBufferData({
              data: exportData,
              name: `Payroll-${formattedStartDate}-${formattedEndDate}`,
              type: exportType,
            });
            customSuccessToast({ message: capitalizeWords(message) });
            handleResetExport();
            if (dropDownRef.current) {
              dropDownRef.current.handleToggleDropdown();
            }
          } else {
            customInfoToast({
              message: NO_EXPORT_DATA_FOUND,
            });
          }
        } else {
          customErrorToast({
            message: "No data returned",
          });
        }
      } catch (error) {
        console.error("Error: ", error);
      }
      setIsExporting(false);
    };

    const filterOptions: TFilterOption[] = [
      {
        type: "checkbox",
        title: "Status",
        key: "status",
        options: [
          {
            label: "All",
            value: "all",
          },
          {
            label: "Draft",
            value: "draft",
          },
          {
            label: "Approved",
            value: "approved",
          },
          {
            label: "In Review",
            value: "in-review",
          },
          {
            label: "Processing",
            value: "processing",
          },
          {
            label: "Rejected",
            value: "rejected",
          },
          {
            label: "Completed",
            value: "completed",
          },
        ],
      },
      {
        type: "date-range",
        title: "Custom Date Range",
        key: "dateRange",
      },
    ];

    useEffect(() => {
      if (!isLoading && filterRef?.current?.isFilterOpen) {
        setIsFiltering(false);
        filterRef?.current?.handleToggleFilter();
      }
    }, [isLoading, filterRef]);

    useEffect(() => {
      if (!isLoading && isRefreshing) {
        handleResetFilter();
        setIsRefreshing(false);
      }
    }, [isLoading]);

    return (
      <Box className="flex px-3 items-center justify-between h-[85px] gap-4 w-[736px]">
        <TableSearchInput
          searchQuery={searchQuery}
          handleSearchQueryChange={handleSearchQueryChange}
        />
        <Filter
          options={filterOptions}
          values={filterValues}
          onChange={handleFilterValueChange}
          onApplyFilter={handleApplyFilter}
          filterBtnCss={tableHeaderCtaCss}
          isLoading={isFiltering}
          ref={filterRef}
          isApplyBtnDisabled={isFilterDisabled}
          handleResetFilter={handleResetFilter}
        />
        <Export
          isLoading={isExporting}
          handleExport={handleExport}
          dateRange={dateRange}
          exportType={exportType}
          handleExportTypeChange={handleExportTypeChange}
          handleDateRangeChange={handleDateRangeChange}
          dropDownRef={dropDownRef}
          availableExportOptions={AVAILABLE_EXPORT_OPTIONS}
          handleResetExport={handleResetExport}
        />
        <Button
          css={tableHeaderCtaCss}
          onClick={!isRefreshing ? handleRefreshTable : undefined}
          disabled={isRefreshing}
        >
          <RefreshIcon
            className={`w-4 h-4 ${isRefreshing ? "animate-spin" : ""}`}
          />
          <p className="text-sm text-[#374151] font-semibold">Refresh</p>
        </Button>
      </Box>
    );
  }
);
